import { cogsRowsWithBandRoundUp } from "@/lib/manufacturing/cogsCatalogView";
import {
  bandRoundUpFromColumns,
  type BandRoundUpIncrement,
} from "@/lib/manufacturing/pricingLayerEvaluate";
import { saveManufacturingPricingBandRoundUp } from "@/lib/manufacturing/pricingLayersClient";
import type { MfgPricingLayerColumn, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";

const OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Off" },
  { value: "1", label: "$1" },
  { value: "5", label: "$5" },
  { value: "10", label: "$10" },
  { value: "25", label: "$25" },
  { value: "50", label: "$50" },
  { value: "100", label: "$100" },
];

type Band = "wholesale" | "msrp";

function columnsWithRoundUp(
  columns: readonly MfgPricingLayerColumn[],
  wholesale: BandRoundUpIncrement | null,
  msrp: BandRoundUpIncrement | null,
): MfgPricingLayerColumn[] {
  return columns.map((column) => {
    const metadata = { ...(column.metadata ?? {}) };
    if (wholesale || msrp) metadata.band_round_up = { wholesale, msrp };
    else delete metadata.band_round_up;
    return { ...column, metadata };
  });
}

export function PricingBandRoundUpSelect({
  band,
  columns,
  rows,
  busy,
  onColumns,
  onRows,
  onError,
  onReload,
}: {
  band: Band;
  columns: MfgPricingLayerColumn[];
  rows: MfgPricingLayerGridRow[];
  busy: boolean;
  onColumns: (columns: MfgPricingLayerColumn[]) => void;
  onRows: (rows: MfgPricingLayerGridRow[]) => void;
  onError: (message: string | null) => void;
  onReload: () => Promise<void>;
}) {
  const current = bandRoundUpFromColumns(columns);
  const selected = current[band];
  return (
    <select
      aria-label={band === "wholesale" ? "Round up wholesale" : "Round up MSRP"}
      title="Round this sum up. MSRP starts from the rounded wholesale."
      className="max-w-[5.5rem] rounded border border-current/20 bg-white/80 px-1 py-0.5 text-[10px] font-normal normal-case tracking-normal text-gray-800 dark:bg-zinc-900 dark:text-zinc-100"
      value={selected == null ? "" : String(selected)}
      disabled={busy}
      onChange={(event) => {
        const increment = event.target.value ? (Number(event.target.value) as BandRoundUpIncrement) : null;
        const next = { ...current, [band]: increment };
        const updated = columnsWithRoundUp(columns, next.wholesale, next.msrp);
        onColumns(updated);
        onRows(cogsRowsWithBandRoundUp(rows, updated));
        onError(null);
        void saveManufacturingPricingBandRoundUp(next).then(async (saved) => {
          if (saved.ok) return;
          onError(saved.error);
          await onReload();
        });
      }}
    >
      {OPTIONS.map((option) => (
        <option key={option.value || "off"} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
